import { Alert } from '@/global/components/Alert'
import Grid from '@/global/components/ui/Grid'
import { encodeFile } from '@acme/common'
import { FormField, FormFieldSelect, FormFile } from 'global/components/Form'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { certificados } from 'service/api'
import { toast } from 'sonner'
import { z } from 'zod'

const schema = z.object({
    nome: z.string().nonempty('Informe um nome'),
    tipo: z.enum(['principal', 'responsavel']),
    arquivo: z.instanceof(File, { message: 'Selecione o certificado' }),
    senha: z.string().nonempty('Informe a senha do certificado'),
    assinanteNome: z.string().optional(),
    assinanteDocumento: z.string().optional(),
})

type FormData = z.infer<typeof schema>

export default function AdicionarCertificado() {
    const { mutateAsync, isLoading } = certificados.mutations.useCreate({
        invalidateQuery: true,
        onSuccess: async () => {
            toast.success('Certificado cadastrado com sucesso!')
        },
        onError: err => {
            toast.error(err.message)
        },
    })

    const handleSubmit: SubmitHandler<FormData> = async ({
        arquivo,
        ...rest
    }) => {
        if (!arquivo) return toast.error('Selecione o certificado')

        return mutateAsync({
            ...rest,
            arquivo: await encodeFile(arquivo),
        } as any)
    }

    return (
        <Dialog.Root
            open={hasPermission(['create', 'Certificados'])}
            onSubmit={handleSubmit}
            schema={schema}
            defaultValues={{ tipo: 'responsavel' }}
            loading={isLoading}
        >
            <Dialog.Title>Adicionar certificado</Dialog.Title>
            <Dialog.Content>
                <Form />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit>
                    Salvar
                </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
        </Dialog.Root>
    )
}

function Form() {
    const { watch } = useFormContext<FormData>()
    const tipo = watch('tipo')

    return (
        <Grid container className="mt-1">
            <Grid item xs={12}>
                <FormField name="nome" label="Nome" autoComplete="off" />
            </Grid>
            <Grid item xs={12}>
                <FormFieldSelect
                    name="tipo"
                    label="Tipo"
                    options={[
                        {
                            value: 'principal',
                            label: 'Principal (certificado da empresa)',
                        },
                        {
                            value: 'responsavel',
                            label: 'Responsável técnico',
                        },
                    ]}
                />
            </Grid>
            {tipo === 'principal' && (
                <Grid item xs={12}>
                    <Alert severity="warning" className="mb-8">
                        Ao criar um novo certificado principal, o atual será
                        substituído
                    </Alert>
                </Grid>
            )}
            {tipo === 'responsavel' && (
                <>
                    <Grid item xs={12} sm={7}>
                        <FormField
                            name="assinanteNome"
                            label="Nome do assinante"
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <FormField
                            name="assinanteDocumento"
                            label="Documento (CPF)"
                            autoComplete="off"
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <FormField
                    name="senha"
                    label="Senha do certificado"
                    autoComplete="off"
                    type="password"
                />
            </Grid>
            <Grid item xs={12} className="mt-2">
                <FormFile
                    name="arquivo"
                    notPreview
                    accept={['.pfx', '.p12', 'application/x-pkcs12']}
                />
            </Grid>
        </Grid>
    )
}
